//vamos a definir la estructura para descargar los informes. 
// Hacer fetch, aplanar datos, y logica de filtrado
// que necesitamos:
//1- Filtros (similar a filtros de la tabla tarmitacion pagos) - crear componente
//2- la tabla (recibe los datos del fetch) - crear componente
//3- paginacion (componente reutilizado) - ya existe el componente Paginacion.jsx
//4- boton de descarga en pdf (descarga todos os registros sin importar la paginacion)


//importemos los componentes que vamos a usar
import React, { useState, useEffect, useMemo, useContext } from 'react';
import FiltrosInformes from './FiltrosInformes';
import TablaInformes_Dinamico from './TablaInformes_Dinamico';
import Paginacion from './Paginacion';
import { optionsPartido } from '../datos/options'; 
import { Button } from './ui';
import { Select_simple } from './ui';
import { Input } from './ui';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AuthContext } from '../context/authContext';
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS_TABLA, getColumnRank } from '../datos/configColumns';
import { barriosJsonATexto } from '../utils/barrios';
import { generarInformePdf } from '../utils/informePdf';

// Función auxiliar para formatear la fecha
const formatDate = (dateString) => {
	if (!dateString) return 'N/A';
	return new Date(dateString).toLocaleDateString('es-AR');
};

// Formatea una fecha 'YYYY-MM-DD' (date de Postgres) sin pasar por Date.
// `new Date('2026-08-06')` es medianoche UTC: con los containers en UTC muestra bien,
// pero en cuanto se setee TZ=America/Argentina/Buenos_Aires (pendiente en AGENTS.md)
// correría un día. Partir el string no depende de la zona horaria.
const fmtDateISO = (d) => d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : 'N/A';

// Año del expediente de Proyecto. Mismo criterio que los tableros y el informe
// de organizaciones: "EX-2021-..." / "EX2021-..." o, si no, el año detrás de "/".
const anioDeExpedienteProyecto = (item) => {
	const exp = (item.array_obras_expediente || []).find(t => t && t.objeto_o === 'Proyecto')?.expediente_o;
	if (!exp) return null;
	const texto = String(exp);
	const conGuion = texto.match(/EX-?([0-9]{4})-/i);
	if (conGuion) return conGuion[1];
	const conBarra = texto.match(/\/([0-9]{2,4})\b/);
	if (conBarra) return conBarra[1].length === 2 ? `20${conBarra[1]}` : conBarra[1];
	return null;
};

// Año del pago del anticipo o, si la obra no tiene anticipo, del pago total.
const anioDePagoAnticipo = (pagosValidos) => {
	const pagos = (pagosValidos || [])
		.filter(p => (p.tipo_pago === 'anticipo' || p.tipo_pago === 'total') && p.fecha_pago)
		.sort((a, b) => {
			const ordenA = a.tipo_pago === 'anticipo' ? 0 : 1;
			const ordenB = b.tipo_pago === 'anticipo' ? 0 : 1;
			if (ordenA !== ordenB) return ordenA - ordenB;
			return String(a.fecha_pago).localeCompare(String(b.fecha_pago));
		});
	return pagos[0]?.fecha_pago ? String(pagos[0].fecha_pago).slice(0, 4) : null;
};

// Montos: punto (.) para miles y coma (,) para decimales (es-AR), siempre con 2
// decimales para no perder centavos. OJO: monto_obra llega como STRING desde
// Postgres (numeric); un string no tiene el toLocaleString de Intl, cae al de
// Object y devuelve el valor crudo ("54952309.88" en vez de "54.952.309,88").
// Por eso se convierte a Number antes de formatear.
const formatearMonto = (valor) => {
	const n = Number(valor);
	if (!Number.isFinite(n)) return 'N/A';
	return `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// 1. Determina los pagos válidos, el total pagado (sin adenda) y el último pago.
const getPagosData = (item) => {
	// Manejo de nulidad para area_responsable
	const area = item.area_responsable || '';

	const pagosArray = area.includes('ÁREA DE PROYECTOS')
		? item.pago_proyectos_jsonb
		: area.includes('ÁREA DE MEJORAS')
			? item.pago_mejoras_jsonb
			: item.pago_microcreditos_jsonb || []; // Incluye Microcréditos si es otra área

	// Pagos Válidos (monto > 0 y fecha_pago)
	const pagosValidos = (pagosArray || []).filter(p => parseFloat(p.monto) > 0 && p.fecha_pago);
	//console.log("PAGOS VALIDOS", pagosValidos)

	// Pagos de la Obra Principal (excluye Adenda)
	const pagosObraPrincipal = pagosValidos.filter(p => p.tipo_pago !== 'adenda');

	// Monto Total Pagado (SOLO obra principal)
	const montoTotalPagadoObra = pagosObraPrincipal.reduce((sum, p) => sum + parseFloat(p.monto), 0);

	// El último pago considera *todos* los pagos (incluida la adenda)
	/*const ultimoPago = pagosValidos.length > 0
		? pagosValidos.sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago))[0]
		: null;*/
	// ⭐️ CORRECCIÓN AQUÍ: El último pago solo considera la obra principal
    const ultimoPago = pagosObraPrincipal.length > 0
        ? pagosObraPrincipal.sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago))[0]
        : null;

    // Obtener el último porcentaje de obra válido (el mayor valor de porcentaje_obra que no sea nulo o indefinido)
    const porcentajesValidos = pagosObraPrincipal
        .map(p => parseFloat(p.porc_avance_obra) || 0)
        .filter(porcentaje => !isNaN(porcentaje) && porcentaje > 0);
    
    const ultimoPorcentajeObra = porcentajesValidos.length > 0
        ? Math.max(...porcentajesValidos)
        : 0; 

	return {
		montoTotalPagado: montoTotalPagadoObra,
		ultimoPago,
        ultimoPorcentajeObra,
		pagosValidos, // Array completo de pagos válidos
		pagosObraPrincipal, // Array de pagos válidos excluyendo Adenda
		pagosTodos: pagosArray || [] // Todos los pagos del área (sin filtrar monto)
	};
};

// 2. Determina la etapa de pago según el porcentaje y el area responsable.
const getEtapaPago = (percentage, area) => {
	//if (area === 'ÁREA DE PROYECTOS') {
		if (percentage === 0) return 'Sin Pago';
		if (percentage > 0 && percentage <= 50) return 'Anticipo';
		if (percentage > 50 && percentage <= 99) return '2do. Pago';
		if (percentage === 100) return 'Total';
		return 'Pago Excedido';
	/*} else if(area === 'AREA DE MEJORAS' || area === 'AREA DE MICROCRÉDITOS') {
		if (percentage === 0) return 'Sin Pago';		
		if (percentage > 0) return 'Pago Total';
		return 'Pago Excedido';
	}*/
};

// Celda compuesta de Avance Financiero: mismo criterio que la tabla original
// (convenio siempre; A1/A2 solo si existe esa adenda)
const avanceFinCelda = (item) => {
	const partes = [`${Math.round(item.detalleConvenio || 0)}%`];
	if (item.tieneAdenda1) partes.push(`A1 ${Math.round(item.detalleAdenda1 || 0)}%`);
	if (item.tieneAdenda2) partes.push(`A2 ${Math.round(item.detalleAdenda2 || 0)}%`);
	return partes.join(' | ');
};

// Función para descargar Excel nativamente (sin dependencias externas)
// Descarga respetando las columnas seleccionadas en la tabla dinámica
const MONTO_COL_IDS_EXCEL = new Set([
	'monto_obra',
	'montoAdenda1',
	'montoAdenda2',
	'montoTotal',
	'montoTotalPagadoGral'
]);

const parsearNumeroMonedaExcel = (valor) => {
	if (valor === undefined || valor === null || valor === '') return null;
	if (typeof valor === 'number' && !isNaN(valor)) return valor;
	if (typeof valor === 'string') {
		let s = valor.trim();
		if (!s) return null;
		s = s.replace(/[^\d,.-]/g, '');
		if (!s) return null;
		if (s.indexOf(',') > -1 && s.indexOf('.') > -1) {
			s = s.replace(/\./g, '').replace(',', '.');
		} else if (s.indexOf(',') > -1 && s.indexOf('.') === -1) {
			s = s.replace(',', '.');
		}
		const num = parseFloat(s);
		return isNaN(num) ? null : num;
	}
	return null;
};

const _xmlEscape = (v) => String(v)
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;');

const _colLetter = (n) => {
	let s = '';
	let x = n + 1;
	while (x > 0) {
		const rem = (x - 1) % 26;
		s = String.fromCharCode(65 + rem) + s;
		x = Math.floor((x - 1) / 26);
	}
	return s;
};

const _crc32 = (bytes) => {
	const table = _crc32._table || (_crc32._table = (() => {
		const t = new Uint32Array(256);
		for (let n = 0; n < 256; n++) {
			let c = n;
			for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
			t[n] = c >>> 0;
		}
		return t;
	})());
	let c = 0xFFFFFFFF;
	for (let i = 0; i < bytes.length; i++) c = table[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
	return (c ^ 0xFFFFFFFF) >>> 0;
};

// Empaqueta archivos en un ZIP sin compresion (metodo store), suficiente para XLSX
const _zipStore = (files) => {
	const enc = new TextEncoder();
	const bytes = [];
	const central = [];
	const writeU16 = (n) => { bytes.push(n & 255, (n >>> 8) & 255); };
	const writeU32 = (n) => { bytes.push(n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255); };
	const writeBytes = (arr) => { for (let i = 0; i < arr.length; i++) bytes.push(arr[i]); };

	files.forEach(({ name, data }) => {
		const nameBytes = enc.encode(name);
		const dataBytes = typeof data === 'string' ? enc.encode(data) : data;
		const crc = _crc32(dataBytes);
		const offset = bytes.length;
		writeU32(0x04034b50);
		writeU16(20); writeU16(0); writeU16(0); writeU16(0); writeU16(0);
		writeU32(crc); writeU32(dataBytes.length); writeU32(dataBytes.length);
		writeU16(nameBytes.length); writeU16(0);
		writeBytes(nameBytes); writeBytes(dataBytes);
		central.push({ nameBytes, crc, size: dataBytes.length, offset });
	});

	const cdStart = bytes.length;
	central.forEach(({ nameBytes, crc, size, offset }) => {
		writeU32(0x02014b50);
		writeU16(20); writeU16(20); writeU16(0); writeU16(0); writeU16(0); writeU16(0);
		writeU32(crc); writeU32(size); writeU32(size);
		writeU16(nameBytes.length); writeU16(0); writeU16(0);
		writeU16(0); writeU16(0); writeU32(0);
		writeU32(offset);
		writeBytes(nameBytes);
	});
	const cdSize = bytes.length - cdStart;
	writeU32(0x06054b50);
	writeU16(0); writeU16(0);
	writeU16(central.length); writeU16(central.length);
	writeU32(cdSize); writeU32(cdStart);
	writeU16(0);

	return new Uint8Array(bytes);
};

const _buildXlsx = ({ headers, rows, sheetName }) => {
	const files = [];
	files.push({
		name: '[Content_Types].xml',
		data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>'
	});
	files.push({
		name: '_rels/.rels',
		data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'
	});
	files.push({
		name: 'xl/workbook.xml',
		data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${_xmlEscape(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`
	});
	files.push({
		name: 'xl/_rels/workbook.xml.rels',
		data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>'
	});
	const styles = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="1"><numFmt numFmtId="164" formatCode="[$$-2C0A]#,##0.00"/></numFmts><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/><bgColor indexed="64"/></patternFill></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>';
	files.push({ name: 'xl/styles.xml', data: styles });

	let sheet = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>';
	const allRows = [headers.map(h => ({ v: h, t: 's', s: 1 })), ...rows];
	allRows.forEach((cells, rIdx) => {
		sheet += `<row r="${rIdx + 1}">`;
		cells.forEach((cell, cIdx) => {
			const ref = `${_colLetter(cIdx)}${rIdx + 1}`;
			if (cell.t === 'n') {
				sheet += `<c r="${ref}" s="${cell.s ?? 0}"><v>${cell.v}</v></c>`;
			} else {
				const styleAttr = cell.s ? ` s="${cell.s}"` : '';
				sheet += `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${_xmlEscape(cell.v ?? '')}</t></is></c>`;
			}
		});
		sheet += '</row>';
	});
	sheet += '</sheetData></worksheet>';
	files.push({ name: 'xl/worksheets/sheet1.xml', data: sheet });

	return _zipStore(files);
};

const downloadExcel = (data = [], role = '', columns = []) => {
	const visibleColumns = (Array.isArray(columns) && columns.length > 0)
		? columns.filter(col => col && col.visible !== false)
		: DEFAULT_COLUMNS_TABLA.filter(col => col.visible !== false).map(col => ({ ...col, label: col.header || col.label }));
	if (visibleColumns.length === 0) return;

	const headers = visibleColumns.map(col => col.label);

	const rows = data.map(item => visibleColumns.map(col => {
		if (col.id === 'detalleConvenio') {
			return { v: avanceFinCelda(item), t: 's' };
		}
		let value = item[col.id];
		if (['avanceFisico', 'ultimoPorcentajeObra'].includes(col.id)) {
			return { v: value == null ? '' : `${Math.round(Number(value))}%`, t: 's' };
		}
		if (col.id === 'beneficiarios' && Array.isArray(value)) {
			return { v: value.join(', '), t: 's' };
		}
		if (MONTO_COL_IDS_EXCEL.has(col.id)) {
			const num = parsearNumeroMonedaExcel(value);
			if (num === null || num === undefined || isNaN(num)) {
				return { v: 0, t: 'n', s: 2 };
			}
			return { v: Number(num.toFixed(2)), t: 'n', s: 2 };
		}
		if (value === undefined || value === null) {
			return { v: '', t: 's' };
		}
		return { v: value, t: 's' };
	}));

	const bytes = _buildXlsx({ headers, rows, sheetName: 'Informes' });
	const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
	const link = document.createElement('a');
	const url = window.URL.createObjectURL(blob);
	link.href = url;
	link.download = `Informes_${role || 'todos'}_${new Date().toISOString().slice(0,10)}.xlsx`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};


const InformesContainer = () => {
    const { role, token } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        encargado_proyecto: '',
        partido_nombre: '',
        
        ong: '',
        area_responsable: '',
        tipo: '',
        subtipo: '',
        tipo_subsidio: '',
        categoria: '',
        programa_lineamiento: '',
        estado: '',
        //search: '',
        objeto: '',
        documentacion: '', //Todos
        etapaPago: '', //Todos
        // ⭐️ NUEVOS FILTROS DE FECHA ⭐️
        fechaInicio: '', 
        fechaFin: '',
        // Año por criterio (pago anticipo/total o año del expediente de Proyecto)
        // y multi-selección de años / gestiones, igual que Tableros y ONGs.
        criterioYear: 'pago_anticipo',
        years: [],
        gestiones: [],
        searchQuery: ''
    });
    //hacer el fetch de los datos
    const [data, setData] = useState([]);
    console.log("TODA LA DATA", data)
    const [datosFiltrados, setDatosFiltrados] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [generandoPdf, setGenerandoPdf] = useState(false);
    const [error, setError] = useState(null);

    // 🔧 Columnas visibles (controladas desde TablaInformes_Dinamico para la descarga)
    const [visibleColumns, setVisibleColumns] = useState([]);

    // Columnas por defecto para el Excel mientras TablaInformes_Dinamico sincroniza
    // las preferencias guardadas en localStorage (respeta orden arrastrado).
    useEffect(() => {
        const defaultColumnIds = DEFAULT_COLUMNS_TABLA;
        const defaultColumns = defaultColumnIds
            .map(id => AVAILABLE_COLUMNS[id] || Object.values(AVAILABLE_COLUMNS).find(c => c.id === id))
            .filter(Boolean);
        setVisibleColumns(defaultColumns);
    }, []);

    useEffect(() => {
        fetch('/api/tabla_informes', {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
        })
            .then(response => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then(data => setData(data))
            .catch(error => {
                console.error('Error fetching informes:', error);
                setError(error.message);
            })
            .finally(() => setIsLoading(false));
    }, [token]);

    //version original
    /*const handleFilterChange = (filterName, value) => {
		setFilters(prevFilters => ({
			...prevFilters,
			[filterName]: value,
		}));
        // Resetear página a 1 cuando cambian filtros
        setCurrentPage(1);
	};*/
    const handleFilterChange = (filterName, value) => {
        setFilters(prevFilters => {
            // 1. Clonamos el estado anterior con el nuevo valor del filtro que cambió
            const nextFilters = {
                ...prevFilters,
                [filterName]: value,
            };

            // 2. TIP EXTRA: Si lo que cambió fue el área responsable, 
            // reiniciamos el programa a un string vacío (o null)
            if (filterName === 'area_responsable') {
                nextFilters.programa_lineamiento = "";
                nextFilters.tipo = "";
                nextFilters.subtipo = "";
            }

            // 3. Si cambió el tipo, reseteamos el subtipo
            if (filterName === 'tipo') {
                nextFilters.subtipo = "";
            }

            // 4. La categoría de ONG solo tiene sentido con "Subsidio a ONG":
            // si se cambia a otro tipo de subsidio, se limpia para no filtrar de más
            if (filterName === 'tipo_subsidio' && value !== 'Subsidio a ONG') {
                nextFilters.categoria = "";
            }

            return nextFilters;
        });

        // Resetear página a 1 cuando cambian filtros
        setCurrentPage(1);
    };

	const limpiarFiltros = () => {
		setFilters({
            encargado_proyecto: '',
            partido_nombre: '',
			
			ong: '',
			area_responsable: '',
			tipo: '',
			subtipo: '',
			tipo_subsidio: '',
			categoria: '',
			programa_lineamiento: '',
			estado: '',
			//search: '',
			objeto: '',
			documentacion: '',
			etapaPago: '',
			fechaInicio: '', 
			fechaFin: '',
			criterioYear: 'pago_anticipo',
			years: [],
			gestiones: [],
			searchQuery: ''
		});
	};  

    // 🌟 Pre-procesamiento y aplanamiento de datos sin desglosar
    const processedData = useMemo(() => {
        return data.map(item => {
            const { montoTotalPagado, ultimoPago, ultimoPorcentajeObra, pagosValidos, pagosTodos } = getPagosData(item);
            const area = item.area_responsable || '';
            const observ_seguimiento_obra = item.observ_seguimiento_obra || "";

            // Expedientes fijos
            //const nroExpProyecto = item.tramitacion_externa_jsonb?.find(t => t.objeto === 'Proyecto')?.exp || 'N/A';
            const nroExpProyecto = item.array_obras_expediente?.find(t => t && t.objeto_o === 'Proyecto')?.expediente_o || 'N/A';



            const nroExpRendicion = item.array_obras_expediente?.find(t => t && t.objeto_o === 'Rendición')?.expediente_o || 'N/A';

            //podria haber dos objetos Adenda, numerar Adenda 1 y Adenda 2 ----> usar array_obras_expediente
            const adendas = item.array_obras_expediente?.filter(t => t && t.objeto_o === 'Adenda') || [];
            const nroExpAdenda1 = adendas[0]?.expediente_o || 'N/A';
            const nroExpAdenda2 = adendas[1]?.expediente_o || 'N/A';
            //Monto para adenda 1 y 2
            const montoAdenda1 = adendas[0]?.monto_adenda ? Number(adendas[0].monto_adenda) : 0;
            const montoAdenda2 = adendas[1]?.monto_adenda ? Number(adendas[1].monto_adenda) : 0;

            // Fechas de caratulación de todos los expedientes en una sola celda.
            // Solo 700 de 1951 expedientes las tienen, por eso van juntas en vez de
            // una columna por expediente (que dejaría la mayoría en blanco).
            const fechasExpediente = [
                ['Proy', item.array_obras_expediente?.find(t => t && t.objeto_o === 'Proyecto')?.fecha_caratulacion_o],
                ['Rend', item.array_obras_expediente?.find(t => t && t.objeto_o === 'Rendición')?.fecha_caratulacion_o],
                ['A1', adendas[0]?.fecha_caratulacion_o],
                ['A2', adendas[1]?.fecha_caratulacion_o],
            ].filter(([, f]) => f).map(([k, f]) => `${k} ${fmtDateISO(f)}`).join(' · ') || 'N/A';

            // Flag de existencia de expediente de adenda (aunque no tenga pagos)
            const tieneAdenda1 = nroExpAdenda1 !== 'N/A';
            const tieneAdenda2 = nroExpAdenda2 !== 'N/A';

            //logica para modalidad: si organismo responsables AREA DE MEJORAS y programa es Mejoras Habitacionales "Trabajar en la Casa" ----> usar etapa
            // en caso contrario siempre usar modalidad
            //formatear item.etapa 1 ---> Etapa I
            //formatear item.etapa 2 ---> Etapa II
            const etapaI = item.etapa === 1 || item.etapa === null || item.etapa === '' ? 'Etapa I' : 'etapa I';
            const etapaII = item.etapa === 2  ? 'Etapa II' : '';

            const usarModalidad = item.area_responsable === 'ÁREA DE MEJORAS' && item.programa_lineamiento === 'Mejoras Habitacionales Trabajar en la Casa' ? (item.etapa === 1 || item.etapa === '' || item.etapa === null ? etapaI : etapaII) : item.modalidad || 'N/A';
            
            // Lógica de Organismo
            let organismo_solicitante = item.organismo_otro;
            if (item.tipo_subsidio === "Subsidio a municipio") {
                organismo_solicitante = `Municipio de ${item.partido_nombre}`;
            } else if (item.tipo_subsidio === "Subsidio a ONG") {
                organismo_solicitante = `ONG ${item.organismo_solicitante}`;
            }

            //logica para multiples barrios (soporta jsonb viejos con {value,label})
            const barriosTexto = item.barrios_rppvap_multiples_jsonb
                ? (barriosJsonATexto(item.barrios_rppvap_multiples_jsonb, ', ') || 'N/A')
                : 'N/A';

            
           const barrioRPPVAP = item.tipos_barrio_jsonb?.find(t => t.value === 'RPPVAP')?.label || 'N/A';
           //lomismo para otro
           const barrioOtro = item.tipos_barrio_jsonb?.find(t => t.value === 'OTRO')?.label || 'N/A';

           //logica monto de obra con un punto separador de miles
           const montoObra = item.monto_obra ? formatearMonto(item.monto_obra) : 'N/A';

           //logica monto total ---> Monto Obra + monto Adenda 1 (si existe) + monto Adenda 2 (si existe)
           //sumar no concatenar
           const montoAdenda1Num = typeof montoAdenda1 === 'string' ? parseFloat(montoAdenda1.replace(/\./g, '')) : montoAdenda1;
           const montoAdenda2Num = typeof montoAdenda2 === 'string' ? parseFloat(montoAdenda2.replace(/\./g, '')) : montoAdenda2;

           //formatear montoAdenda1 y montoAdenda2
           const montoAdenda1Formateado = montoAdenda1 ? formatearMonto(montoAdenda1) : 'N/A';
           const montoAdenda2Formateado = montoAdenda2 ? formatearMonto(montoAdenda2) : 'N/A';
           

           //LOgica monto total ----> sumar como numero monto conveniado + montoAdenda1 como numero + montoAdenda2 como numero
           //si alguno es N/A, sumar 0
           // ERROR $1000000	100	200 lo esta sumando como -----> 1000000100200
            let montoTotal = 0;
            montoTotal += parseFloat(item.monto_obra) || 0; // Convertir a número
            montoTotal += montoAdenda1Num || 0;
            montoTotal += montoAdenda2Num || 0;

            //Formatear monto total con $ y separador de mil para mostrar en l atabla
            const montoTotalFormateado = formatearMonto(montoTotal);

           // Avance financiero % = (monto total pagado / monto total con adendas) * 100
           const montoTotalNumerico = montoTotal > 0 ? montoTotal : (parseFloat(item.monto_obra) || 0);
           const montoTotalPagadoConAdendas = (pagosValidos || []).reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
           const avanceFinanciero = montoTotalNumerico > 0 ? Math.min((montoTotalPagadoConAdendas / montoTotalNumerico) * 100, 100) : 0;

           // Desglose del avance financiero por concepto (conveniado / adenda1 / adenda2)
           // Pagos de la obra principal (conveniado) sin adendas
           const pagosConvenio = (pagosValidos || []).filter(p => p.tipo_pago !== 'adenda');
           const montoPagadoConvenio = pagosConvenio.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
           // Pagos de adenda, en orden (luego adenda1 el primero, adenda2 el segundo)
           const pagosAdendasOrden = (pagosValidos || [])
               .filter(p => p.tipo_pago === 'adenda')
               .sort((a, b) => (parseFloat(a.orden) || 0) - (parseFloat(b.orden) || 0));
           const montoPagadoAdenda1 = pagosAdendasOrden[0] ? parseFloat(pagosAdendasOrden[0].monto) || 0 : 0;
           const montoPagadoAdenda2 = pagosAdendasOrden[1] ? parseFloat(pagosAdendasOrden[1].monto) || 0 : 0;

           const porcPorcentajeDe = (monto) => montoTotalNumerico > 0 ? Math.min((monto / montoTotalNumerico) * 100, 100) : 0;
           const avanceConvenio = porcPorcentajeDe(montoPagadoConvenio);
           const avanceAdenda1 = porcPorcentajeDe(montoPagadoAdenda1);
           const avanceAdenda2 = porcPorcentajeDe(montoPagadoAdenda2);

           // Detalle financiero (opción 2): adenda es 0% (sin pago) o 100% (pagada), convenio proporcional sobre su propio monto
           const detalleConvenio = (parseFloat(item.monto_obra) || 0) > 0
               ? Math.min((montoPagadoConvenio / (parseFloat(item.monto_obra) || 0)) * 100, 100)
               : 0;
           const detalleAdenda1 = (montoAdenda1Num > 0 && montoPagadoAdenda1 > 0) ? 100 : 0;
           const detalleAdenda2 = (montoAdenda2Num > 0 && montoPagadoAdenda2 > 0) ? 100 : 0;
           
            
            

            // --- LÓGICA DE RENDICIÓN ---
            const tramiteRendicion = item.tramitacion_externa_jsonb?.find(t => t.objeto === 'Rendición');
            let rendicionData = { exp: 'N/A', montoConvenido: 'N/A', avance: 0, etapa: 'N/A' };

            if (tramiteRendicion) {
                const montoObra = parseFloat(item.monto_obra) || 0;
                let avance = montoObra > 0 ? Math.min((montoTotalPagado / montoObra) * 100, 100) : 0;

                rendicionData = {
                    exp: tramiteRendicion.exp,
                    montoConvenido: formatearMonto(montoObra),
                    avance: avance,
                    etapa: getEtapaPago(avance, area)
                };
            }

            // --- LÓGICA DE ADENDA --- 

            
            let tramiteAdenda = item.tramitacion_externa_jsonb?.find(t => t.objeto === 'Adenda');
            let adendaData = { exp: 'N/A', montoConvenido: 'N/A', montoPagado: 'N/A', avance: 0, etapa: 'N/A' };

            if (tramiteAdenda) { //para el primer objeto adenda
                const pagosArray = area.includes('ÁREA DE PROYECTOS') ? item.pago_proyectos_jsonb : item.pago_mejoras_jsonb; //un solo pagosArray
                const registroAdenda = pagosArray?.find(p => p.tipo_pago === 'adenda');

                if (registroAdenda) {
                    const montoTotalAdenda = parseFloat(registroAdenda.total_adenda || registroAdenda.monto) || 0;
                    const montoPagadoAdenda = parseFloat(registroAdenda.monto) || 0;
                    let avanceAdenda = montoTotalAdenda > 0 ? (montoPagadoAdenda / montoTotalAdenda) * 100 : 0;

                    let etapaAdenda = 'Pendiente';
                    if (avanceAdenda >= 100) {
                        etapaAdenda = montoPagadoAdenda > montoTotalAdenda ? 'PAGO EXCEDIDO' : 'Pagado Totalmente';
                        avanceAdenda = Math.min(avanceAdenda, 100);
                    } else if (avanceAdenda > 0) {
                        etapaAdenda = 'Pago Parcial';
                    }

                    adendaData = {
                        exp: tramiteAdenda.exp,
                        montoConvenido: montoTotalAdenda > 0 ? formatearMonto(montoTotalAdenda) : 'N/A',
                        montoPagado: formatearMonto(montoPagadoAdenda),
                        avance: avanceAdenda,
                        etapa: etapaAdenda
                    };
                }
            }

            // Años para el filtro de gestión (se calculan acá y se filtran abajo)
            const anioPagoAnticipo = anioDePagoAnticipo(pagosTodos);
            const anioExpedienteProyecto = anioDeExpedienteProyecto(item);

            // Retorno de objeto único por item
            return {
                ...item,
                id: item.id,
                modalidad: usarModalidad,
                nombreObra: item.nombre_obra,
                nroExpProyecto: nroExpProyecto,
                nroExpRendicion: nroExpRendicion,
                nroExpAdenda1: nroExpAdenda1,
                nroExpAdenda2: nroExpAdenda2,
                tieneAdenda1: tieneAdenda1,
                tieneAdenda2: tieneAdenda2,
                
                organismo_solicitante: organismo_solicitante,
                ong_categoria: item.ong_categoria || '',
                barrio_rppvap: barrioRPPVAP,                
                barrios_rppvap_multiples: barriosTexto,

                barrio_otro: barrioOtro,
                barrio_otro_nombre: (item.barrio_otro_nombre_jsonb || []).join(', ') || item.barrio_otro_nombre || 'N/A',

                // Agrupación de datos de Rendición
                rendicion_exp: rendicionData.exp,
                rendicion_montoConvenido: rendicionData.montoConvenido,
                rendicion_avance: rendicionData.avance,
                rendicion_etapa: rendicionData.etapa,

                // Agrupación de datos de Adenda
                //adenda_exp: adendaData.exp,
                //adenda_montoConvenido: adendaData.montoConvenido,
                //adenda_montoPagado: adendaData.montoPagado,
                //adenda_avance: adendaData.avance,
                //adenda_etapa: adendaData.etapa,

                // Datos generales
                monto_obra: montoObra, //monto conveniado de la obra principal

                montoAdenda1: montoAdenda1Formateado,
                montoAdenda2: montoAdenda2Formateado,

                montoTotal: montoTotalFormateado,

                // Montos SIN formatear, para el PDF (que arma el desglose de adenda
                // como el informe por partido y necesita sumar, no mostrar strings).
                monto_obra_num: parseFloat(item.monto_obra) || 0,
                montoAdenda1_num: montoAdenda1Num || 0,
                montoAdenda2_num: montoAdenda2Num || 0,
                montoTotal_num: montoTotalNumerico,

                montoTotalPagadoGral: formatearMonto(montoTotalPagado),
                fecha_carga: fmtDateISO(item.fecha_carga),
                fechasExpediente: fechasExpediente,
                ultimoPago: ultimoPago ? formatDate(ultimoPago.fecha_pago) : 'N/A',
                ultimoPagoISO: ultimoPago ? ultimoPago.fecha_pago : null,
                anioPagoAnticipo,
                anioExpedienteProyecto,
                observaciones: observ_seguimiento_obra,
                ultimoPorcentajeObra: ultimoPorcentajeObra,
                avanceFisico: item.avance_fisico != null ? Math.min(Number(item.avance_fisico), 100) : 0,
                avanceFinanciero: avanceFinanciero,
                avanceConvenio: avanceConvenio,
                avanceAdenda1: avanceAdenda1,
                avanceAdenda2: avanceAdenda2,
                detalleConvenio: detalleConvenio,
                detalleAdenda1: detalleAdenda1,
                detalleAdenda2: detalleAdenda2
            };
        });
    }, [data]);

    // 🔄 Lógica de Filtrado
    const filteredData = useMemo(() => {
        let currentData = processedData;
        const { fechaInicio, fechaFin, ong, searchQuery } = filters; // Desestructuramos los nuevos filtros

        //filtro por Encargado de Proyecto
        if(filters.encargado_proyecto){
            currentData = currentData.filter(item => item.encargado_proyecto === filters.encargado_proyecto)
        }

        // Filtro por partido
        if (filters.partido_nombre) {
            const partidoSeleccionado = optionsPartido.find(
                p => p.value === filters.partido_nombre
            );
            if (partidoSeleccionado) {
                currentData = currentData.filter(
                    item => item.partido_nombre === partidoSeleccionado.label
                );
            }
        }

        // Filtro por ONG/Cooperativa
        if (ong) {
            currentData = currentData.filter(item => 
                item.organismo_solicitante && 
                item.organismo_solicitante.toLowerCase().includes(ong.toLowerCase())
            );
        }

        // Filtro por categoría de la ONG (religiosa/deportiva/...). Solo las obras
        // con ongs_id traen categoría, así que es implícitamente "solo ONGs".
        if (filters.categoria) {
            currentData = currentData.filter(item =>
                (item.ong_categoria || '').toLowerCase() === filters.categoria.toLowerCase()
            );
        }

        //Logica de filtrado por area responsable
        if (filters.area_responsable) {
            currentData = currentData.filter(item =>
                item.area_responsable && item.area_responsable.includes(filters.area_responsable)
            );
        }

        // Filtro por tipo de obra (depende del área responsable)
        if (filters.tipo) {
            currentData = currentData.filter(item =>
                (item.array_tipo_subtipo_proyecto || []).some(ts => ts.tipo === filters.tipo)
            );
        }

        // Filtro por subtipo de obra (depende del tipo)
        if (filters.subtipo) {
            currentData = currentData.filter(item =>
                (item.array_tipo_subtipo_proyecto || []).some(ts => ts.subtipo === filters.subtipo)
            );
        }
        
        if (filters.tipo_subsidio) {
            currentData = currentData.filter(item => item.tipo_subsidio === filters.tipo_subsidio);                       
        }

        if (filters.programa_lineamiento) {
            currentData = currentData.filter(
                item => item.programa_lineamiento === filters.programa_lineamiento
            );
        }

        if (filters.estado) {
            currentData = currentData.filter(item => item.estado === filters.estado);
        }
        

        // 1. Filtro por Objeto
        if (filters.objeto) {
            currentData = currentData.filter(item => item.objeto === filters.objeto);
        }

        // 2. Filtro por Documentación
        if (filters.documentacion) { //!== 'Todos'
            currentData = currentData.filter(item => {
                const { resolucion, documentacion_pago_ok } = item.tramitacionInterna;
                switch (filters.documentacion) {
                    case 'Resolución':
                        return resolucion === true;
                    case 'Documentación para el pago':
                        return documentacion_pago_ok === true;
                    case 'Ambas':
                        return resolucion === true && documentacion_pago_ok === true;
                    default:
                        return true;
                }
            });
        }

        // 3. Filtro por Etapa de pago (solo Rendición)
        if (filters.etapaPago) {//!== 'Todos'
            currentData = currentData.filter(item => {
                if (item.objeto !== 'Rendición') return false;

                const totalPagado = parseFloat(item.montoTotalPagado.replace('$', '').replace(/\./g, '').replace(',', '.'));
                const montoObra = item.montoObraRendicion;
                if (montoObra === 0) return false;

                const porcentajePagado = (totalPagado / montoObra) * 100;

                switch (filters.etapaPago) {
                    case 'anticipo':
                        return item.pagosValidos.some(p => p.tipo_pago === 'anticipo');//&& porcentajePagado > 0 && porcentajePagado <= 50
                    case '2do_pago':
                        return item.pagosValidos.some(p => p.tipo_pago === '2do_pago'); // && porcentajePagado > 50 && porcentajePagado <= 99
                    case '3er_pago':
                        return item.pagosValidos.some(p => p.tipo_pago === '3er_pago'); //&& porcentajePagado > 80 && porcentajePagado < 100
                    case 'total':
                        return item.pagosValidos.some(p => p.tipo_pago === 'total');//|| porcentajePagado === 100
                    default:
                        return false;
                }
            });
        }

        // Filtro por año según el criterio elegido (pago anticipo/total o
        // expediente de Proyecto). Los años se eligen de a varios, y las
        // gestiones solo expanden a sus años.
        if (Array.isArray(filters.years) && filters.years.length > 0) {
            const campo = filters.criterioYear === 'expediente'
                ? 'anioExpedienteProyecto'
                : 'anioPagoAnticipo';
            const yearsSeleccionados = new Set(filters.years.map(String));
            currentData = currentData.filter(
                item => item[campo] && yearsSeleccionados.has(String(item[campo]))
            );
        }

        // 4. Filtro por Rango de Fechas (Ultimo Pago)
        if (fechaInicio || fechaFin) {
            currentData = currentData.filter(item => {
                const pagoDate = item.ultimoPagoISO ? new Date(item.ultimoPagoISO) : null;

                // Si no hay pago, no se incluye en el filtro de fechas.
                if (!pagoDate) return false;

                let cumpleInicio = true;
                let cumpleFin = true;

                // Compara con Fecha de Inicio
                if (fechaInicio) {
                    // Aseguramos que la fecha de inicio es el comienzo del día
                    const start = new Date(fechaInicio);
                    start.setHours(0, 0, 0, 0);
                    cumpleInicio = pagoDate >= start;
                }

                // Compara con Fecha de Fin
                if (fechaFin) {
                    // Aseguramos que la fecha de fin es el final del día
                    const end = new Date(fechaFin);
                    end.setHours(23, 59, 59, 999);
                    cumpleFin = pagoDate <= end;
                }

                return cumpleInicio && cumpleFin;
            });
        }

        // ⭐️ 5. Filtro por Búsqueda de Texto (Search Query) ⭐️
        if (searchQuery) {
            const query = searchQuery.toLowerCase().trim();

            currentData = currentData.filter(item => {
                // Campos a buscar: Nombre de obra, Nro. Exp. Proyecto, nombre de Partido.
                const idPartido = item.id_partido_obra?.toString().toLowerCase() || '';
                const nombreObra = item.nombreObra?.toLowerCase() || '';
                const nroExpProyecto = item.nroExpProyecto?.toLowerCase() || '';
                // Necesitamos acceder al nombre del partido original. 
                // Para esto, debemos asegurarnos de agregarlo en processedData.

                // Si no lo tienes aún en processedData, asegúrate de agregarlo en el paso A:
                // "partidoNombre: item.partido_nombre,"
                const partidoNombre = item.partidoNombre?.toLowerCase() || '';

                return (
                    idPartido.includes(query) ||
                    nombreObra.includes(query) ||
                    nroExpProyecto.includes(query) ||
                    partidoNombre.includes(query)
                );
            });
        }

        setDatosFiltrados(currentData);
        return currentData;
    }, [processedData, filters, setDatosFiltrados]);
    
    //logica de paginacion
    const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredData.length / itemsPerPage);

	// Resumen legible de los filtros activos, para el subtítulo del PDF
	const resumenFiltros = useMemo(() => {
		const etiquetas = {
			encargado_proyecto: 'Encargado', partido_nombre: 'Partido', ong: 'ONG',
			area_responsable: 'Área', tipo: 'Tipo', subtipo: 'Subtipo',
			tipo_subsidio: 'Tipo de subsidio', categoria: 'Categoría',
			programa_lineamiento: 'Programa', estado: 'Estado', objeto: 'Objeto',
			fechaInicio: 'Desde', fechaFin: 'Hasta', searchQuery: 'Búsqueda',
		};
		// El filtro de partido guarda el CÓDIGO (p.ej. "32"); para el informe se muestra
		// el nombre (p.ej. "Florencio Varela"), que es el label de optionsPartido.
		const valorLegible = (clave, valor) => {
			if (clave === 'partido_nombre') {
				const opcion = optionsPartido.find((p) => p.value === valor);
				return opcion ? opcion.label : valor;
			}
			return valor;
		};
		const partes = Object.entries(etiquetas)
			.filter(([clave]) => filters[clave])
			.map(([clave, etiqueta]) => `${etiqueta}: ${valorLegible(clave, filters[clave])}`);
		// Los años se muestran aparte según el criterio elegido (no entran en el
		// listado de arriba porque un array vacío también es "truthy").
		if (Array.isArray(filters.years) && filters.years.length > 0) {
			const criterio = filters.criterioYear === 'expediente'
				? 'Año expediente de Proyecto'
				: 'Año pago (anticipo o total)';
			const years = [...filters.years].sort((a, b) => String(b).localeCompare(String(a)));
			partes.push(`${criterio}: ${years.join(', ')}`);
		}
		return partes.length ? partes.join(' · ') : 'Todos los registros';
	}, [filters]);

	const handleDescargarPdf = async () => {
		if (filteredData.length === 0) {
			alert('No hay datos para exportar');
			return;
		}
		setGenerandoPdf(true);
		try {
			await generarInformePdf({
				obras: filteredData,
				tituloResumen: resumenFiltros,
				nombreArchivo: `informe-${new Date().toISOString().slice(0, 10)}`,
			});
		} catch (err) {
			console.error('Error al generar el PDF', err);
			alert('No se pudo generar el PDF: ' + (err?.message || 'error desconocido'));
		} finally {
			setGenerandoPdf(false);
		}
	};

    return (
        <div>
            {/* Banda de encabezado con el mismo patrón que usa la página de Contactos
                (ContactosONGPage.jsx:896-901), para que las dos se vean iguales. El título
                vive acá y no en InformesPage, que tenía además un "Descarga Informes"
                redundante y este <h1> sin className, o sea con el estilo del browser. */}
            <div className="flex flex-col p-4 mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                    Informes
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Descargá la tabla en Excel, o personalizá qué columnas se ven y en qué orden.
                </p>
            </div>
            <FiltrosInformes 
                data={processedData}
                datosFiltrados={datosFiltrados}
                filters={filters}
                onFilterChange={handleFilterChange}               
                limpiarFiltros={limpiarFiltros}
            />
            {/* La tabla personalizada (TablaInformes_Dinamico) la usan TODOS los roles,
                no solo admin. Antes el ternario mandaba a los no-admin a TablaInformes
                (la tabla fija de siempre), y como "Personalizar columnas" vive solo en
                la dinámica, no aparecía para nadie más que para el admin.
                Todos ven las mismas columnas por defecto que admin y el Excel baja
                respetando lo que cada uno ve en pantalla.
                TablaInformes.jsx y downloadExcelOriginal quedan sin uso (son respaldo). */}

            <div className="flex items-center justify-between mb-2 gap-3">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Tabla de Informes</h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => downloadExcel(filteredData, role, visibleColumns)}
                        className="inline-flex items-center px-4 py-2 border border-teal-600 text-sm font-medium rounded-md shadow-sm text-teal-700 bg-white hover:bg-teal-50"
                    >
                        Descargar Excel
                    </button>
                    <button
                        onClick={handleDescargarPdf}
                        disabled={generandoPdf || filteredData.length === 0}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                    >
                        {generandoPdf ? 'Generando PDF...' : 'Descargar PDF'}
                    </button>
                </div>
            </div>

            <TablaInformes_Dinamico
                data={currentItems}
                visibleColumns={visibleColumns}
                onColumnsChange={setVisibleColumns}
                isLoading={isLoading}
            />

            <Paginacion
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={filteredData.length}
            />
        </div>
    );
};

export default InformesContainer;
